import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Alert, Box, Center } from '@chakra-ui/react';
import React, { useContext, useEffect, useMemo } from 'react';
import { fetchCurrentUserRoles } from '../clients/zitadel-gql';
import { ANONYMOUS_AUTH, isAuthFlowRoute, needsOidcRuntime, OidcAuthContext, rememberReturnTo } from './auth-context';
import { AuthUserProvider } from './auth-user';
import { useNotificationsContext } from './notifications';
const OidcRuntime = React.lazy(async () => await import('./auth-oidc'));
const ROLES_CLAIM = __JAEN_ZITADEL_GQL__.rolesClaim ?? 'urn:zitadel:iam:org:project:roles';
const rolesFromTokenClaim = (claim) => {
    const items = Array.isArray(claim) ? claim : claim ? [claim] : [];
    return items.flatMap(item => {
        if (typeof item === 'string')
            return [item];
        return item && typeof item === 'object' ? Object.keys(item) : [];
    });
};
const RolesContext = React.createContext({
    roles: [],
    isRolesLoading: false
});
export const RolesProvider = ({ children }) => {
    const oidcAuth = useContext(OidcAuthContext) ?? ANONYMOUS_AUTH;
    const [isRolesLoading, setIsRolesLoading] = React.useState(false);
    const [roles, setRoles] = React.useState([]);
    useEffect(() => {
        let cancelled = false;
        const getRoles = async () => {
            const claimRoles = rolesFromTokenClaim(oidcAuth.user?.profile[ROLES_CLAIM]);
            setRoles(prev => Array.from(new Set([...prev, ...claimRoles])));
            setIsRolesLoading(true);
            try {
                const { plainRoles, projectScopedRoles } = await fetchCurrentUserRoles(oidcAuth.user.access_token);
                if (cancelled)
                    return;
                setRoles(Array.from(new Set([...projectScopedRoles, ...plainRoles, ...claimRoles])));
            }
            catch (error) {
                console.error('jaen: zitadel-gql roles query failed', error);
            }
            finally {
                if (!cancelled) {
                    setIsRolesLoading(false);
                }
            }
        };
        if (oidcAuth.user) {
            void getRoles();
        }
        return () => {
            cancelled = true;
        };
    }, [oidcAuth.user]);
    const value = useMemo(() => ({ roles, isRolesLoading }), [roles, isRolesLoading]);
    return _jsx(RolesContext.Provider, { value: value, children: children });
};
export const useAuth = () => {
    const oidcAuth = useContext(OidcAuthContext) ?? ANONYMOUS_AUTH;
    const { roles, isRolesLoading } = useContext(RolesContext);
    const auth = useMemo(() => {
        return {
            ...oidcAuth,
            user: {
                ...oidcAuth.user,
                roles
            },
            isLoading: isRolesLoading || oidcAuth.isLoading
        };
    }, [oidcAuth, roles, isRolesLoading]);
    return auth;
};
export const checkUserRoles = (user, roles) => {
    if (!user)
        return false;
    return roles.some(role => user.roles.includes(role));
};
export const AuthenticationProvider = ({ children, pathname }) => {
    const scope = useMemo(() => {
        if (__JAEN_ZITADEL_GQL__.scope)
            return __JAEN_ZITADEL_GQL__.scope;
        const parts = new Set();
        parts.add('openid');
        parts.add('profile');
        parts.add('email');
        if (__JAEN_ZITADEL_GQL__.organizationId) {
            const projectIds = [
                ...(__JAEN_ZITADEL_GQL__.projectIds || []),
                'zitadel'
            ];
            parts.add(`urn:zitadel:iam:org:id:${__JAEN_ZITADEL_GQL__.organizationId}`);
            projectIds.forEach(projectId => {
                parts.add(`urn:zitadel:iam:org:project:id:${projectId}:aud`);
            });
        }
        parts.add('offline_access');
        return Array.from(parts).join(' ');
    }, []);
    const [needsOidc, setNeedsOidc] = React.useState(false);
    useEffect(() => {
        if (needsOidc)
            return;
        if (!needsOidcRuntime(pathname))
            return;
        let cancelled = false;
        void import('./auth-oidc')
            .then(() => {
            if (!cancelled)
                setNeedsOidc(true);
        })
            .catch(error => {
            console.error('jaen: could not load the OIDC runtime', error);
        });
        return () => {
            cancelled = true;
        };
    }, [pathname, needsOidc]);
    if (needsOidc) {
        return (_jsx(React.Suspense, { fallback: null, children: _jsx(OidcRuntime, { clientId: __JAEN_ZITADEL_GQL__.clientId, redirectUri: __JAEN_ZITADEL_GQL__.redirectUri, authority: __JAEN_ZITADEL_GQL__.authority, scope: scope, children: _jsx(RolesProvider, { children: _jsx(AuthUserProvider, { children: children }) }) }) }));
    }
    return (_jsx(OidcAuthContext.Provider, { value: ANONYMOUS_AUTH, children: _jsx(AuthUserProvider, { children: children }) }));
};
export const withAuthSecurity = (Component) => {
    const Wrapper = props => {
        const pageConfigAuth = props.pageContext.pageConfig?.auth;
        const auth = useAuth();
        const notify = useNotificationsContext();
        const loadingText = useMemo(() => {
            switch (auth.activeNavigator) {
                case 'signinRedirect':
                case 'signinSilent':
                    return 'Signing you in...';
                case 'signoutSilent':
                case 'signoutRedirect':
                    return 'Signing you out...';
                case undefined:
                    return undefined;
                default:
                    return 'Loading...';
            }
        }, [auth.activeNavigator]);
        useEffect(() => {
            if (auth.error) {
                notify.toast({
                    title: 'Error',
                    description: auth.error.message,
                    status: 'error'
                });
            }
        }, [auth.error, notify]);
        useEffect(() => {
            if (loadingText) {
                notify.toast({
                    title: loadingText,
                    status: 'info'
                });
            }
        }, [loadingText]);
        useEffect(() => {
            if (!pageConfigAuth?.isRequired)
                return;
            if (auth.isLoading || auth.isAuthenticated)
                return;
            if (auth.activeNavigator)
                return;
            if (!auth.isRuntimeLoaded && needsOidcRuntime(window.location.pathname)) {
                return;
            }
            if (isAuthFlowRoute(window.location.pathname))
                return;
            rememberReturnTo(window.location.pathname + window.location.search);
            window.location.assign('/login');
        }, [
            pageConfigAuth?.isRequired,
            auth.isLoading,
            auth.isAuthenticated,
            auth.activeNavigator,
            auth.isRuntimeLoaded
        ]);
        if (pageConfigAuth?.isRequired) {
            let roles = pageConfigAuth?.roles;
            if (pageConfigAuth.isAdminRequired) {
                if (!roles) {
                    roles = ['jaen:admin'];
                }
                else {
                    roles.push('jaen:admin');
                }
            }
            if (roles) {
                const hasRoles = checkUserRoles(auth.user, roles);
                if (!hasRoles) {
                    return (_jsx(Center, { height: "100vh", children: _jsx(Box, { textAlign: "center", children: _jsxs(Alert.Root, { status: "error", mb: 4, children: [_jsx(Alert.Indicator, {}), "You don't have the required roles to view this page"] }) }) }));
                }
            }
            if (auth.isAuthenticated) {
                return _jsx(Component, { ...props });
            }
            return null;
        }
        return _jsx(Component, { ...props });
    };
    return Wrapper;
};
